import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes, FaFileWord, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  PARSE_SUPPLIER_CATALOG,
  parseCatalogFromFile,
  importCatalogProducts,
} from '../utils/supplierCatalog';
import '../styles/InventoryProducts.css';
import '../styles/InventoryProcurement.css';

export default function SupplierCatalogModal({
  supplier,
  suppliers = [],
  onClose,
  onSaved,
}) {
  const [supplierId, setSupplierId] = useState(supplier?.id || '');
  const [fileName, setFileName] = useState('');
  const [fileBase64, setFileBase64] = useState('');
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const selectedSupplier = useMemo(
    () =>
      suppliers.find((s) => s.id === supplierId) ||
      (supplier?.id === supplierId ? supplier : null),
    [suppliers, supplier, supplierId],
  );

  const parsePayload = async (payload) => {
    setParsing(true);
    try {
      const data = await graphqlRequest(
        PARSE_SUPPLIER_CATALOG,
        { parseSupplierCatalogInput: payload },
        { timeout: 120000 },
      );
      const parsed = Array.isArray(data?.parseSupplierCatalog)
        ? data.parseSupplierCatalog
        : [];
      if (!parsed.length) {
        toast.error(
          'No products with prices found in that file. Use a table or lines like “Bleach 5L $12.50”.',
        );
        setRows([]);
        return;
      }
      setRows(
        parsed.map((r, i) => ({
          ...r,
          key: `${i}-${r.name}`,
          selected: true,
        })),
      );
      toast.success(
        `Found ${parsed.length} product${parsed.length === 1 ? '' : 's'}`,
      );
    } catch (err) {
      toast.error(err?.message || 'Could not read the Word file');
    } finally {
      setParsing(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setRows([]);
    setParsing(true);
    try {
      const parsed = await parseCatalogFromFile(file);
      if (!parsed.length) {
        toast.error(
          'No products with prices found in that file. Use a table or lines like “Bleach 5L $12.50”.',
        );
        setRows([]);
        return;
      }
      setFileBase64('');
      setText('');
      setRows(parsed);
      toast.success(
        `Found ${parsed.length} product${parsed.length === 1 ? '' : 's'}`,
      );
    } catch (err) {
      toast.error(err?.message || 'Could not read the Word file');
    } finally {
      setParsing(false);
    }
  };

  useEffect(() => {
    if (supplier?.id) setSupplierId(supplier.id);
  }, [supplier?.id]);

  const handleImport = async () => {
    if (!supplierId) {
      toast.error('Choose which supplier this price list belongs to');
      return;
    }
    const products = rows.filter((r) => r.selected && String(r.name || '').trim());
    if (!products.length) {
      toast.error('Select at least one product to import');
      return;
    }
    setImporting(true);
    try {
      const res = await importCatalogProducts(supplierId, rows);
      const created = Number(res?.created) || 0;
      const merged = Number(res?.merged) || 0;
      const failed = Number(res?.failed) || 0;
      if (created + merged > 0) {
        toast.success(
          `Imported ${created} new product${created === 1 ? '' : 's'}${
            merged ? `, updated ${merged}` : ''
          }${failed ? ` (${failed} failed)` : ''}`,
        );
        onSaved?.();
        onClose?.();
      } else {
        toast.error(failed ? 'Import failed' : 'Nothing was imported');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to import products');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="iproc-overlay" onClick={onClose}>
      <div
        className="iproc-modal iproc-modal--wide"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="iproc-modal-header">
          <h2>
            Upload Word catalog
            {selectedSupplier?.name ? ` — ${selectedSupplier.name}` : ''}
          </h2>
          <button
            type="button"
            className="iproc-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>
        <div className="iproc-modal-body">
          <p className="ip-bulk-hint">
            Drop the supplier’s Word price list (.docx). We read product names
            and prices from the file so you do not type each item.
          </p>

          {!supplier?.id ? (
            <label className="iproc-field" style={{ marginBottom: 12 }}>
              <span>Supplier *</span>
              <select
                className="iproc-select"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
              >
                <option value="">Select supplier</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div
            className={`iproc-drop${dragOver ? ' is-over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              void handleFile(e.dataTransfer.files?.[0]);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
            }}
          >
            <FaFileWord />
            <strong>
              {parsing ? 'Reading Word file…' : 'Drop Word file here'}
            </strong>
            <span>or click to choose .docx, CSV or TXT</span>
            {fileName ? <em>{fileName}</em> : null}
            <input
              ref={inputRef}
              type="file"
              accept=".docx,.doc,.csv,.txt,.tsv"
              hidden
              onChange={(e) => void handleFile(e.target.files?.[0])}
            />
          </div>

          <label className="iproc-field" style={{ marginTop: 14 }}>
            <span>Or paste the product list</span>
            <textarea
              rows={5}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setFileBase64('');
                setFileName('');
              }}
              placeholder={
                'Bleach 5L $12.50\nNitrile Gloves (Box) $8.90\nDisinfectant, 4.50, Litre'
              }
            />
          </label>
          {text.trim() && !fileBase64 ? (
            <div
              className="iproc-modal-footer"
              style={{ border: 0, padding: '8px 0 0' }}
            >
              <button
                type="button"
                className="iproc-btn iproc-btn--primary"
                onClick={() => void parsePayload({ text: text.trim() })}
                disabled={parsing}
              >
                {parsing ? 'Reading…' : 'Read pasted list'}
              </button>
            </div>
          ) : null}

          {rows.length > 0 ? (
            <>
              <div className="ip-bulk-table-wrap" style={{ maxHeight: 280 }}>
                <table className="ip-bulk-table">
                  <thead>
                    <tr>
                      <th />
                      <th>Product</th>
                      <th>Price</th>
                      <th>Unit</th>
                      <th>Category</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key}>
                        <td>
                          <input
                            type="checkbox"
                            checked={row.selected}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.key === row.key
                                    ? { ...r, selected: e.target.checked }
                                    : r,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={row.name}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.key === row.key
                                    ? { ...r, name: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.perItemCost}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.key === row.key
                                    ? { ...r, perItemCost: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={row.unit}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.key === row.key
                                    ? { ...r, unit: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={row.category}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.key === row.key
                                    ? { ...r, category: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            value={row.quantity}
                            onChange={(e) =>
                              setRows((prev) =>
                                prev.map((r) =>
                                  r.key === row.key
                                    ? { ...r, quantity: e.target.value }
                                    : r,
                                ),
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="iproc-modal-footer">
                <span className="ip-bulk-count">
                  {rows.filter((r) => r.selected).length} selected
                </span>
                <button type="button" className="iproc-btn" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="iproc-btn iproc-btn--primary"
                  onClick={() => void handleImport()}
                  disabled={importing}
                >
                  <FaUpload />
                  {importing ? ' Importing…' : ' Add products to inventory'}
                </button>
              </div>
            </>
          ) : (
            <div className="iproc-modal-footer">
              <button type="button" className="iproc-btn" onClick={onClose}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
